<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="ln-head" data-reveal>
        <p class="ln-eyebrow">Dashboard</p>
        <h1 class="ln-display">Account</h1>
        <p class="ln-lede">Update your profile, email and password.</p>
      </header>

      <div class="ln-sections">
        <!-- Verify identity -->
        <section
          v-if="isPasswordAccount"
          class="ln-panel ln-section"
          data-reveal
        >
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-shield-lock-outline</v-icon>
            <h2 class="ln-section__title">Verify your identity</h2>
          </div>
          <div class="ln-section__body">
            <p class="ln-section__lede">
              Enter your current password before saving any change below.
            </p>
            <div class="ln-field ln-field--narrow">
              <v-text-field
                v-model="currentPassword"
                :append-inner-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show1 ? 'text' : 'password'"
                label="Current password"
                autocomplete="current-password"
                variant="outlined"
                density="compact"
                hide-details="auto"
                @click:append-inner="show1 = !show1"
                data-testid="account-current-password"
              ></v-text-field>
            </div>
          </div>
        </section>

        <!-- Profile -->
        <section class="ln-panel ln-section" data-reveal>
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-account-outline</v-icon>
            <h2 class="ln-section__title">Profile</h2>
          </div>
          <div class="ln-section__body">
            <div class="ln-grid-2">
              <div class="ln-field">
                <v-text-field
                  v-model="displayName"
                  label="First name"
                  autocomplete="name"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  data-testid="account-display-name"
                ></v-text-field>
              </div>
              <div class="ln-field">
                <v-text-field
                  v-model="email"
                  label="Email"
                  autocomplete="email"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :disabled="!isPasswordAccount"
                  :hint="
                    isPasswordAccount ? '' : 'Managed by your Google account'
                  "
                  persistent-hint
                  data-testid="account-email"
                ></v-text-field>
              </div>
            </div>
            <p
              v-if="pendingEmail"
              class="ln-section__lede ln-section__note"
              data-testid="account-pending-email"
            >
              We sent a confirmation link to <strong>{{ pendingEmail }}</strong
              >. Your email changes once you click it, and you will then be
              asked to sign in again with the new address. If nothing arrives,
              check your spam folder, or the address may already have an
              account.
            </p>
          </div>
          <div class="ln-section__footer">
            <button
              type="button"
              class="ln-btn ln-btn--solid"
              :disabled="savingProfile"
              @click="updateProfileOrEmail"
            >
              <v-icon
                size="small"
                class="ln-btn__icon"
                :class="{ 'ln-spin': savingProfile }"
                >{{
                  savingProfile ? "mdi-loading" : "mdi-content-save"
                }}</v-icon
              >
              {{ savingProfile ? "Saving" : "Update profile" }}
            </button>
          </div>
        </section>

        <!-- Password -->
        <section
          v-if="isPasswordAccount"
          class="ln-panel ln-section"
          data-reveal
        >
          <div class="ln-section__head">
            <v-icon class="ln-section__icon">mdi-lock-outline</v-icon>
            <h2 class="ln-section__title">Password</h2>
          </div>
          <div class="ln-section__body">
            <div class="ln-grid-2">
              <div class="ln-field">
                <v-text-field
                  v-model="newPassword.input1"
                  :append-inner-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show2 ? 'text' : 'password'"
                  label="New password"
                  autocomplete="new-password"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  @click:append-inner="show2 = !show2"
                ></v-text-field>
              </div>
              <div class="ln-field">
                <v-text-field
                  v-model="newPassword.input2"
                  :append-inner-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="show2 ? 'text' : 'password'"
                  label="Confirm new password"
                  autocomplete="new-password"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  @click:append-inner="show2 = !show2"
                ></v-text-field>
              </div>
            </div>
          </div>
          <div class="ln-section__footer">
            <button
              type="button"
              class="ln-btn ln-btn--solid"
              :disabled="savingPassword"
              @click="updatePassword"
            >
              <v-icon
                size="small"
                class="ln-btn__icon"
                :class="{ 'ln-spin': savingPassword }"
                >{{
                  savingPassword ? "mdi-loading" : "mdi-content-save"
                }}</v-icon
              >
              {{ savingPassword ? "Saving" : "Update password" }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  auth,
  verifyBeforeUpdateEmail,
  updatePassword,
  updateProfile,
  reauthenticate,
} from "../auth";

export default {
  computed: {
    ...mapGetters(["getUserEmail", "getUserName"]),
  },
  data() {
    return {
      email: null,
      // address a confirmation link was sent to, not the account's yet
      pendingEmail: null,
      displayName: null,
      show1: false,
      show2: false,
      currentPassword: null,
      newPassword: {
        input1: null,
        input2: null,
      },
      savingProfile: false,
      savingPassword: false,
      // google-only accounts have no password to re-authenticate with
      isPasswordAccount: this.hasPasswordLogin(),
    };
  },
  mounted() {
    // load values (deep copy)
    this.email = this.getUserEmail;
    this.displayName = this.getUserName;
    this.$store.commit("setIsLoading", false);
    this.setupReveal();
  },
  beforeUnmount() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    setupReveal() {
      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
    hasPasswordLogin() {
      const user = auth().currentUser;
      if (!user) return true;
      return user.providerData.some((p) => p && p.providerId === "password");
    },
    async reauthenticate() {
      try {
        await reauthenticate(this.currentPassword);
        return true;
      } catch {
        window.alert("Error, Entered password not valid");
        return false;
      }
    },
    async updateEmail() {
      // Validate email format first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.email || !emailRegex.test(this.email.trim())) {
        this.$store.commit("showSnackbarMessage", {
          content: "Please enter a valid email address.",
          color: "error",
        });
        return false;
      }

      const success = await this.reauthenticate();

      if (success) {
        try {
          const newEmail = this.email.trim();
          await verifyBeforeUpdateEmail(newEmail);
          // nothing has changed on the account yet: the address only switches
          // once the link is clicked. Back to the current one, so the next
          // save does not send the link again
          this.pendingEmail = newEmail;
          this.email = this.getUserEmail;
          this.$store.commit("showSnackbarMessage", {
            content: "Confirmation link sent to " + newEmail,
            color: "success",
          });
          return true;
        } catch (error) {
          let errorMessage = "Failed to update email. ";

          if (error.code === "auth/invalid-email") {
            errorMessage += "Please enter a valid email address.";
          } else if (error.code === "auth/email-already-in-use") {
            errorMessage += "That address already has an account.";
          } else if (error.code === "auth/requires-recent-login") {
            errorMessage += "Please re-enter your password and try again.";
          } else if (error.code === "auth/too-many-requests") {
            errorMessage += "Too many attempts. Please try again later.";
          } else {
            errorMessage += error.message || "Please try again.";
          }

          this.$store.commit("showSnackbarMessage", {
            content: errorMessage,
            color: "error",
          });
          return false;
        }
      }
      return false;
    },
    async updateProfileOrEmail() {
      const trimmedEmail = (this.email || "").trim();
      const trimmedName = (this.displayName || "").trim();
      const emailChanged = !!trimmedEmail && trimmedEmail !== this.getUserEmail;
      const nameChanged = !!trimmedName && trimmedName !== this.getUserName;

      if (!emailChanged && !nameChanged) {
        this.$store.commit("showSnackbarMessage", {
          content: "No changes to save",
        });
        return;
      }

      this.savingProfile = true;
      try {
        let emailUpdated = false;

        if (emailChanged) {
          emailUpdated = await this.updateEmail();
        }

        if (nameChanged) {
          const ok = await this.updateProfile();
          if (ok !== false && !emailChanged) {
            this.$store.commit("showSnackbarMessage", {
              content: "Profile updated successfully",
            });
          } else if (ok !== false && !emailUpdated) {
            // the name save no longer depends on the password, so it can
            // succeed after the email change failed
            this.$store.commit("showSnackbarMessage", {
              content: "Name updated, but the email was not changed.",
              color: "warning",
            });
          }
        }
        if (emailUpdated) {
          this.currentPassword = null;
        }
      } finally {
        this.savingProfile = false;
      }
    },
    async updateProfile() {
      // no re-authentication: firebase does not require a recent login for
      // updateProfile, and a google-only account has no password to enter
      const displayName = (this.displayName || "").trim();
      if (!displayName) return false;
      try {
        await updateProfile({ displayName });
        // the store holds a snapshot of the user: left as it was, this page
        // would show the old name again the next time it is opened, and take
        // a change back to that name for "no changes"
        this.$store.commit("setUser", auth().currentUser);
        return true;
      } catch (error) {
        this.$store.commit("showSnackbarMessage", {
          content:
            error.message || "Failed to update profile. Please try again.",
          color: "error",
        });
        return false;
      }
    },
    async updatePassword() {
      this.savingPassword = true;
      try {
        const success = await this.reauthenticate();
        if (!success) return false;
        if (!this.newPassword.input1 || !this.newPassword.input2) {
          window.alert(
            "Error, one or more data entered is not valid, check your input and try again",
          );
          return false;
        }
        if (this.newPassword.input1 !== this.newPassword.input2) {
          window.alert(
            "Error, one or more data entered is not valid, check your input and try again",
          );
          return false;
        }
        const password = this.newPassword.input2;
        try {
          await updatePassword(password);
          this.$store.commit("showSnackbarMessage", {
            content: "Password updated successfully",
            color: "success",
          });
          return true;
        } catch (error) {
          this.$store.commit("showSnackbarMessage", {
            content:
              error.message || "Failed to update password. Please try again.",
            color: "error",
          });
          return false;
        }
      } finally {
        this.savingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

.ln-sections [data-reveal]:nth-child(2) {
  transition-delay: 80ms;
}

.ln-sections [data-reveal]:nth-child(3) {
  transition-delay: 160ms;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- buttons ---------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.ln-spin {
  animation: ln-spin 900ms linear infinite;
}

@keyframes ln-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ---------- sections ---------------------------------------------------- */

.ln-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ln-panel {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
}

.ln-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-section__icon.v-icon {
  font-size: 19px;
  color: var(--ln-muted);
}

.ln-section__title {
  margin: 0;
  flex: 1 1 auto;
  font-family: sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-section__body {
  padding: 20px;
}

.ln-section__lede {
  margin: 0 0 16px;
  max-width: 60ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

/* a lede placed under the fields instead of above them */
.ln-section__note {
  margin: 16px 0 0;
}

.ln-section__footer {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 20px;
}

/* ---------- fields ------------------------------------------------------ */

.ln-grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.ln-field--narrow {
  max-width: 320px;
}

.ln-section__body :deep(.v-field__outline) {
  color: var(--ln-line);
  --v-field-border-opacity: 1;
}

.ln-section__body :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
  --v-field-border-opacity: 1;
}

.ln-section__body :deep(.v-label) {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
}

.ln-section__body :deep(.v-field__input) {
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
}

.ln-section__body :deep(.v-messages__message) {
  font-size: 11.5px;
}

/* ---------- breakpoints ------------------------------------------------ */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-section__head {
    padding: 20px 26px;
  }

  .ln-section__body {
    padding: 26px;
  }

  .ln-section__footer {
    padding: 0 26px 26px;
  }

  .ln-grid-2 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }
}
</style>
